import { IStyleConfig, mergeStyles } from '@cian/utils/lib/shared/style';
import { isYesterday, isToday } from 'date-fns';
import * as React from 'react';

import * as styles from './PrettyDate.css';

const UPDATER_COMPONENTS: Map<PrettyDateContainer, () => void> = new Map();

interface IPrettyDateProps {
  date: number;
  showOnlyTime?: boolean;
  showTime?: boolean;
  showFullDateWithTime?: boolean;
  containerStyles?: IStyleConfig;
}

interface IPrettyDateState {
  text: string;
}

interface IParsedDate {
  day: number;
  hours: number;
  minutes: number;
  month: number;
  seconds: number;
  year: number;
  timestamp: number;
}

const monthDict = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
];

interface IPrettyDateObject {
  day: number | string;
  hours: string | number;
  minutes: string | number;
  month: string;
  prettyDate: string | number;
  prettyTime: string;
  year: number | null;
}

const getDate = (parsedDate: IParsedDate): IPrettyDateObject => {
  const currentParsedDate = parseDate(new Date());

  const day = isToday(parsedDate.timestamp) ? 'Сегодня' : isYesterday(parsedDate.timestamp) ? 'Вчера' : parsedDate.day;
  const year = currentParsedDate.year > parsedDate.year ? parsedDate.year : null;
  const month = monthDict[parsedDate.month];
  const hours = parsedDate.hours < 10 ? `0${parsedDate.hours}` : parsedDate.hours;
  const minutes = parsedDate.minutes < 10 ? `0${parsedDate.minutes}` : parsedDate.minutes;

  const prettyDate = isNaN(day as number) ? day : `${day} ${month}${year ? ` ${year}` : ''}`;
  const prettyTime = `${hours}:${minutes}`;

  return {
    day,
    hours,
    minutes,
    month,
    prettyDate,
    prettyTime,
    year,
  };
};

function parseDate(date: Date): IParsedDate {
  return {
    day: date.getDate(),
    hours: date.getHours(),
    minutes: date.getMinutes(),
    month: date.getMonth(),
    seconds: date.getSeconds(),
    timestamp: date.getTime(),
    year: date.getFullYear(),
  };
}

const getPrettyTimeString = (
  dateObject: IPrettyDateObject,
  {
    showOnlyTime,
    showTime,
    showFullDateWithTime,
  }: {
    showOnlyTime?: boolean;
    showFullDateWithTime?: boolean;
    showTime?: boolean;
  },
): string => {
  let prettyDate = showOnlyTime ? dateObject.prettyTime : dateObject.prettyDate;

  if (showTime && !showOnlyTime) {
    if (!dateObject.year) {
      prettyDate += `, ${dateObject.prettyTime}`;
    } else if (showFullDateWithTime) {
      prettyDate += `, ${dateObject.prettyTime}`;
    }
  }

  return prettyDate as string;
};

// TODO: задача на рефакторинг https://jira.cian.tech/browse/CD-189527
export class PrettyDateContainer extends React.Component<IPrettyDateProps, IPrettyDateState> {
  private date: Date;

  public constructor(props: IPrettyDateProps) {
    super(props);

    this.date = new Date(props.date);

    this.state = {
      text: getPrettyTimeString(getDate(parseDate(this.date)), {
        showFullDateWithTime: props.showFullDateWithTime,
        showOnlyTime: props.showOnlyTime,
        showTime: props.showTime,
      }),
    };
  }

  public componentDidUpdate(prevProps: IPrettyDateProps) {
    if (this.props.date !== prevProps.date) {
      this.date = new Date(this.props.date);
      this.setState({
        text: getPrettyTimeString(getDate(parseDate(this.date)), {
          showFullDateWithTime: this.props.showFullDateWithTime,
          showOnlyTime: this.props.showOnlyTime,
          showTime: this.props.showTime,
        }),
      });
    }
  }

  public componentDidMount() {
    UPDATER_COMPONENTS.set(this, this.updateTime);
  }

  public componentWillUnmount() {
    UPDATER_COMPONENTS.delete(this);
  }

  public shouldComponentUpdate(nextProps: IPrettyDateProps, nextState: IPrettyDateState) {
    return this.state.text !== nextState.text;
  }

  public render() {
    return <span {...mergeStyles(styles['container'], this.props.containerStyles)}>{this.state.text}</span>;
  }

  private updateTime = () => {
    const { showOnlyTime, showTime, showFullDateWithTime } = this.props;

    this.setState({
      text: getPrettyTimeString(getDate(parseDate(this.date)), { showOnlyTime, showTime, showFullDateWithTime }),
    });
  };
}
